import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, setCsrfToken, unwrap } from "./client";

export type SessionState = Awaited<ReturnType<typeof fetchSession>>;
export type ProgramDetail = Awaited<ReturnType<typeof fetchProgram>>;
export type RunDetail = Awaited<ReturnType<typeof fetchRun>>;
export type ParameterSpec = ProgramDetail["current"] extends infer V ? (V extends { parameters: Array<infer P> } ? P : never) : never;

/** How often to look again while work is queued or running. */
const POLL_MS = 2_500;
const BUSY = new Set(["queued", "running", "authoring"]);

async function fetchSession() {
  const session = unwrap(await api.GET("/auth/session"));
  setCsrfToken(session.csrfToken);
  return session;
}

async function fetchProgram(programId: string) {
  return unwrap(await api.GET("/api/v1/programs/{programId}", { params: { path: { programId } } }));
}

async function fetchRun(runId: string) {
  return unwrap(await api.GET("/api/v1/runs/{runId}", { params: { path: { runId } } }));
}

export const keys = {
  session: ["session"] as const,
  programs: ["programs"] as const,
  program: (id: string) => ["programs", id] as const,
  runs: (programId: string) => ["programs", programId, "runs"] as const,
  run: (id: string) => ["runs", id] as const,
  competitions: ["pickers", "competitions"] as const,
  fixtures: (competitionId: string, date: string, timeZone: string) => ["pickers", "fixtures", competitionId, date, timeZone] as const,
};

export function useSessionState() {
  return useQuery({ queryKey: keys.session, queryFn: fetchSession, staleTime: 60_000 });
}

export function usePrograms(enabled: boolean) {
  return useQuery({
    queryKey: keys.programs,
    queryFn: async () => unwrap(await api.GET("/api/v1/programs")),
    enabled,
    refetchInterval: (query) => (query.state.data?.some((program) => BUSY.has(program.status)) ? POLL_MS : false),
  });
}

export function useProgram(programId: string) {
  return useQuery({
    queryKey: keys.program(programId),
    queryFn: () => fetchProgram(programId),
    refetchInterval: (query) =>
      query.state.data && (BUSY.has(query.state.data.status) || query.state.data.sessions.some((session) => BUSY.has(session.status))) ? POLL_MS : false,
  });
}

export function useRuns(programId: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.runs(programId),
    queryFn: async () => unwrap(await api.GET("/api/v1/programs/{programId}/runs", { params: { path: { programId } } })),
    enabled,
    refetchInterval: (query) => (query.state.data?.some((run) => BUSY.has(run.status)) ? POLL_MS : false),
  });
}

export function useRun(runId: string | undefined) {
  return useQuery({
    queryKey: keys.run(runId ?? ""),
    queryFn: () => fetchRun(runId as string),
    enabled: Boolean(runId),
    refetchInterval: (query) => (query.state.data && BUSY.has(query.state.data.status) ? POLL_MS : false),
  });
}

export function useCompetitions(enabled: boolean) {
  return useQuery({
    queryKey: keys.competitions,
    queryFn: async () => unwrap(await api.GET("/api/v1/pickers/competitions")),
    enabled,
    staleTime: Infinity,
  });
}

export function useFixtures(competitionId: string, date: string, timeZone: string) {
  return useQuery({
    queryKey: keys.fixtures(competitionId, date, timeZone),
    queryFn: async () => unwrap(await api.GET("/api/v1/pickers/fixtures", { params: { query: { competitionId, date, timeZone } } })),
    enabled: Boolean(competitionId && date && timeZone),
    staleTime: 5 * 60_000,
  });
}

export function useCreateProgram() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (request: string) => unwrap(await api.POST("/api/v1/programs", { body: { request } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.programs }),
  });
}

export function useReviseProgram(programId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (request: string) =>
      unwrap(await api.POST("/api/v1/programs/{programId}/revisions", { params: { path: { programId } }, body: { request } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.program(programId) }),
  });
}

export function useStartRun(programId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (params: Record<string, string | number | boolean>) =>
      unwrap(await api.POST("/api/v1/programs/{programId}/runs", { params: { path: { programId } }, body: { params } })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.runs(programId) }),
  });
}

export function useSignOut() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const result = await api.POST("/auth/logout");
      if (!result.response.ok) unwrap(result);
    },
    onSuccess: async () => {
      await client.cancelQueries();
      const current = client.getQueryData<SessionState>(keys.session);
      setCsrfToken(null);
      if (current) client.setQueryData<SessionState>(keys.session, {
        ...current, authenticated: false, user: null, csrfToken: null, denied: null,
        login: { ...current.login, demo: current.login.demo ? { ...current.login.demo, csrfToken: null } : undefined },
      });
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== keys.session[0] });
      await client.invalidateQueries({ queryKey: keys.session });
    },
    onError: () => client.invalidateQueries({ queryKey: keys.session }),
  });
}

/** The pre-login proof is distinct from an authenticated session's CSRF token. */
export function useDemoSignIn() {
  const client = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: async ({ username, password, csrf }: { username: string; password: string; csrf: string }) =>
      unwrap(await api.POST("/auth/demo/login", { body: { username, password }, headers: { "x-analyst-csrf": csrf } })),
    onSuccess: async (state) => {
      await client.cancelQueries();
      // Preserve the active session observer while removing all prior-user data.
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== keys.session[0] });
      setCsrfToken(state.csrfToken);
      client.setQueryData(keys.session, state);
      // A trial user may have arrived at a team-only deep link.
      if (window.location.pathname !== "/" || window.location.search) window.location.replace("/");
    },
  });
}
